import { HTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

type GapSize = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;

const gapMap: Record<GapSize, string> = {
    0: 'gap-0',
    1: 'gap-1',
    2: 'gap-2',
    3: 'gap-3',
    4: 'gap-4',
    5: 'gap-5',
    6: 'gap-6',
    8: 'gap-8',
    10: 'gap-10',
    12: 'gap-12',
};

interface StackProps extends HTMLAttributes<HTMLDivElement> {
    gap?: GapSize;
    align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
    justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
    wrap?: boolean;
}

/**
 * VStack — A vertical flex column. Items stack top to bottom.
 */
export const VStack = forwardRef<HTMLDivElement, StackProps>(
    ({ gap = 4, align = 'stretch', justify = 'start', wrap = false, className, children, ...rest }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    'flex flex-col',
                    gapMap[gap],
                    `items-${align}`,
                    `justify-${justify}`,
                    wrap && 'flex-wrap',
                    className
                )}
                {...rest}
            >
                {children}
            </div>
        );
    }
);
VStack.displayName = 'VStack';

/**
 * HStack — A horizontal flex row. Items stack left to right.
 */
export const HStack = forwardRef<HTMLDivElement, StackProps>(
    ({ gap = 4, align = 'center', justify = 'start', wrap = false, className, children, ...rest }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    'flex flex-row',
                    gapMap[gap],
                    `items-${align}`,
                    `justify-${justify}`,
                    wrap && 'flex-wrap',
                    className
                )}
                {...rest}
            >
                {children}
            </div>
        );
    }
);
HStack.displayName = 'HStack';
